import { useState } from "react";
import { FileText, DollarSign, Calendar, Clock, ArrowUpRight, ChevronDown, BarChart3 } from "lucide-react";

export default function CompensationPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleExpand = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Sample data
  const paymentHistory = [
    { id: 1, date: "May 12, 2025", amount: "₹23,500", status: "Completed", recipient: "John Doe" },
    { id: 2, date: "Apr 28, 2025", amount: "₹45,200", status: "Completed", recipient: "Jane Smith" },
    { id: 3, date: "Apr 15, 2025", amount: "₹19,800", status: "Completed", recipient: "Mark Wilson" },
    { id: 4, date: "Mar 30, 2025", amount: "₹32,700", status: "Completed", recipient: "Sarah Johnson" },
  ];

  const compensationPackages = [
    { 
      id: 1, 
      title: "Executive Package", 
      amount: "₹1.2 - 1.8 Cr", 
      benefits: ["Stock Options", "Quarterly Bonuses", "Health Insurance", "Retirement Plan"],
      eligibility: "C-Suite Executives, Board Members" 
    },
    { 
      id: 2, 
      title: "Senior Management", 
      amount: "₹45 - 85 Lakhs", 
      benefits: ["Annual Bonuses", "Health Insurance", "Retirement Plan", "Performance Incentives"],
      eligibility: "Directors, Senior Managers" 
    },
    { 
      id: 3, 
      title: "Mid-level Management", 
      amount: "₹25 - 40 Lakhs", 
      benefits: ["Health Insurance", "Retirement Plan", "Training Budget", "Flexible Work Hours"],
      eligibility: "Team Leads, Project Managers" 
    },
    { 
      id: 4, 
      title: "Staff Package", 
      amount: "₹8 - 20 Lakhs", 
      benefits: ["Health Insurance", "Retirement Plan", "Education Assistance", "Wellness Program"],
      eligibility: "Associates, Specialists, Analysts" 
    },
  ];

  const metrics = [
    { label: "Total Disbursed", value: "₹187 Cr", change: "+12% YoY", icon: <DollarSign className="text-green-500" size={20} /> },
    { label: "Avg. Processing Time", value: "2.3 Days", change: "-15% YoY", icon: <Clock className="text-blue-500" size={20} /> },
    { label: "Annual Budget", value: "₹210 Cr", change: "+8% YoY", icon: <Calendar className="text-purple-500" size={20} /> },
    { label: "Compensation Ratio", value: "24.5%", change: "+2.1% YoY", icon: <BarChart3 className="text-orange-500" size={20} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <FileText className="text-yellow-400 mr-3" size={32} />
          <h1 className="text-3xl font-bold text-gray-800">Compensation</h1>
        </div>
        <div className="px-4 py-2 bg-yellow-100 rounded-lg flex items-center">
          <span className="text-yellow-700 font-semibold">₹187 Cr Disbursed</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-8 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            className={`pb-4 px-1 ${
              activeTab === "overview"
                ? "border-b-2 border-yellow-400 text-yellow-600 font-medium"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`pb-4 px-1 ${
              activeTab === "packages"
                ? "border-b-2 border-yellow-400 text-yellow-600 font-medium"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("packages")}
          >
            Compensation Packages
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === "overview" && (
        <div>
          {/* Hero Banner */}
          <div className="w-full h-64 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-xl mb-8 p-8 flex items-center justify-between">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-2">Compensation</h2>
              <p className="text-yellow-100 mb-4 max-w-xl">
                Manage and track all compensation activities
              </p>
              <button className="bg-white text-yellow-600 px-4 py-2 rounded-lg font-medium flex items-center">
                Generate Reports
                <ArrowUpRight className="ml-2" size={16} />
              </button>
            </div>
            <div className="hidden md:block">
              {/* Fixed image size with proper aspect ratio and object fit */}
              <div className="w-48 h-48 overflow-hidden rounded-lg">
                <img 
                  src="/home image/compensation.jpg" 
                  alt="Compensation illustration" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics.map((metric, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-gray-500 font-medium">{metric.label}</h3>
                  {metric.icon}
                </div>
                <div className="flex items-end">
                  <p className="text-2xl font-bold text-gray-800">{metric.value}</p>
                  <span className={`ml-2 text-sm ${metric.change.includes('+') ? 'text-green-500' : 'text-red-500'}`}>
                    {metric.change}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Featured Packages Preview */}
          <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Compensation Packages</h2>
              <button 
                className="text-yellow-600 hover:text-yellow-700 font-medium flex items-center"
                onClick={() => setActiveTab("packages")}
              >
                View All
                <ArrowUpRight className="ml-1" size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {compensationPackages.slice(0, 2).map((pkg) => (
                <div key={pkg.id} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-800">{pkg.title}</h3>
                  <p className="text-yellow-600 font-bold mt-1">{pkg.amount}</p>
                  <p className="text-gray-500 text-sm mt-2">Eligibility: {pkg.eligibility}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Payments Preview */}
          {/* <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Recent Payments</h2>
              <button 
                className="text-yellow-600 hover:text-yellow-700 font-medium flex items-center"
              >
                View History
                <ArrowUpRight className="ml-1" size={16} />
              </button>
            </div>
          </div> */}
        </div>
      )}

      {activeTab === "packages" && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Compensation Packages</h2>
            <div className="space-y-4">
              {compensationPackages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div 
                    className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleExpand(pkg.id)}
                  >
                    <div>
                      <h3 className="font-medium text-gray-800">{pkg.title}</h3>
                      <p className="text-yellow-600 font-bold mt-1">{pkg.amount}</p>
                    </div>
                    <ChevronDown 
                      className={`text-gray-400 transition-transform ${expandedCard === pkg.id ? 'transform rotate-180' : ''}`} 
                      size={20} 
                    />
                  </div>
                  {expandedCard === pkg.id && (
                    <div className="p-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-700 mb-2"><span className="font-medium">Eligibility:</span> {pkg.eligibility}</p>
                      <div>
                        <p className="font-medium text-gray-700 mb-1">Benefits:</p>
                        <ul className="list-disc list-inside text-gray-600 pl-2">
                          {pkg.benefits.map((benefit, idx) => (
                            <li key={idx}>{benefit}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Compensation Distribution Chart */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 mb-6"></h2>
            <div className="flex items-center justify-center">
              {/* Fixed dimensions with proper aspect ratio */}
              <div className="w-full h-64 relative">
                <img 
                  src="/home image/Compensation distribution chart.webp" 
                  alt="Compensation distribution chart" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination section */}
      {/* <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-500">Showing 8 of 24 transactions</p>
        <div className="flex items-center space-x-2">
          <button className="border border-gray-300 rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-50">Previous</button>
          <button className="bg-yellow-500 text-white rounded-md px-3 py-1 text-sm hover:bg-yellow-600">1</button>
          <button className="border border-gray-300 rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-50">2</button>
          <button className="border border-gray-300 rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-50">3</button>
          <button className="border border-gray-300 rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-50">Next</button>
        </div>
      </div> */}
    </div>
  );
}